import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { Partner, PartnerCountry } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import AddPartnerView from './partner-add-view';
import { EnchancedPartnerCountry } from '../../utils/partners/types';

const formatCardData = (
  countries: PartnerCountry[],
  countryCodes: CountryCodeMap,
): EnchancedPartnerCountry[] => countries.map((s: PartnerCountry) => ({
  country: s.country,
  primary: s.primary,
  minBudgetPPPD: s.minBudgetPPPD,
  displayName: countryCodes[s.country],
}));

const AddPartnerContainer = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { mutate: mutatePartners } = useFetch<Partner[]>(apiClient.partnersClient.getPartners, [], true);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    getValues,
    setValue,
  } = useForm();

  const [submissionError, setSubmissionError] = useState<ApiError>();
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [countryToEdit, setCountryToEdit] = useState<PartnerCountry>();
  const [countryToRemove, setCountryToRemove] = useState<EnchancedPartnerCountry>();

  const countries = getValues('countries');

  const cardData: EnchancedPartnerCountry[] = useMemo(
    () => formatCardData(countries || [], countryCodes || {}),
    [countryCodes, countries],
  );

  // Generate Map of countries in use
  const excludedCountries: CountryCodeMap | any = useMemo(() => {
    if (!countryCodes) return {};
    if (!countries || !countries.length) return {};
    return countries.reduce((acc: CountryCodeMap, cur: PartnerCountry) => {
      if (!acc[cur.country]) acc[cur.country] = countryCodes[cur.country];
      return acc;
    }, {});
  }, [countryCodes, countries]);

  const handleOpenCountryModal = () => setCountryModalOpen(true);
  const handleCloseCountryModal = () => {
    setCountryToEdit(undefined);
    setCountryModalOpen(false);
  };

  const handleCancel = () => {
    navigate('/partners');
  };

  const handleAddOrUpdateCountry = (partnerCountry: PartnerCountry) => {
    const countriesList = getValues('countries') || [];

    if (countryToEdit) {
      const existingIndex = countriesList.findIndex((c: PartnerCountry) => c.country === countryToEdit.country);
      countriesList[existingIndex] = partnerCountry;
    } else {
      countriesList.push(partnerCountry);
    }
    setCountryToEdit(undefined);
    setValue('countries', countriesList);
  };

  const handleEditCountry = (partnerCountry: EnchancedPartnerCountry) => {
    const ret: PartnerCountry = {
      country: partnerCountry.country,
      minBudgetPPPD: partnerCountry.minBudgetPPPD,
      primary: partnerCountry.primary,
    };
    setCountryToEdit(ret);
    setCountryModalOpen(true);
  };

  const handleClickRemoveCountry = (partnerCountry: EnchancedPartnerCountry) => {
    setCountryToRemove(partnerCountry);
  };

  const handleConfirmRemoveCountry = () => {
    let countriesList = getValues('countries') || [];
    if (countryToRemove) {
      countriesList = countriesList.filter((pc: PartnerCountry) => pc.country !== countryToRemove.country);
      setValue('countries', countriesList);
    }
    setCountryToRemove(undefined);
  };

  const handleCancelRemoveCountry = () => setCountryToRemove(undefined);

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    try {
      const partner: Partner = await apiClient.partnersClient.postPartner(data as Partner);
      enqueueSnackbar('Partner added.', { variant: 'success' });
      // add partner to state
      mutatePartners();
      navigate(`/partners/${partner.id}`);
    } catch (error) {
      setSubmissionError(error as ApiError);
    }
  };

  return (
    <AddPartnerView
      countries={cardData || []}
      countryModalOpen={countryModalOpen}
      countryToEdit={countryToEdit}
      countryToRemove={countryToRemove}
      errors={errors}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={isValid}
      onAddCountry={handleAddOrUpdateCountry}
      onCancel={handleCancel}
      onCancelRemoveCountry={handleCancelRemoveCountry}
      onClickEditCountry={handleEditCountry}
      onClickRemoveCountry={handleClickRemoveCountry}
      onCloseCountryModal={handleCloseCountryModal}
      onConfirmRemoveCountry={handleConfirmRemoveCountry}
      onOpenCountryModal={handleOpenCountryModal}
      onSubmit={onSubmit}
      register={register}
      submissionError={submissionError}
      excludeCountries={excludedCountries}
    />
  );
};

export default AddPartnerContainer;
